import {
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  ElementRef,
  ViewChildren,
  QueryList,
  AfterViewInit,
} from '@angular/core';
import { ToastService, Toast } from '../../../core/services/toast.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.css'],
})
export class ToastComponent implements OnDestroy {
  private toastService = inject(ToastService);

  // Map of toast ID → interval timer
  private countdownTimers = new Map<number, ReturnType<typeof setInterval>>();
  // Track which toasts we've already started timers for
  private trackedIds = new Set<number>();

  // Expose signals from service
  readonly toasts = this.toastService.visibleToasts;
  readonly hasToasts = this.toastService.hasToasts;

  // Per-toast countdown progress (0-100)
  readonly countdownProgress = signal<Map<number, number>>(new Map());

  constructor() {
    // Watch for toast changes and manage timers
    effect(
      () => {
        const currentToasts = this.toasts();
        const currentIds = new Set(currentToasts.map(t => t.id));

        // Clean up timers for removed toasts
        for (const [id] of this.countdownTimers) {
          if (!currentIds.has(id)) {
            this.clearTimer(id);
          }
        }

        // Start timers for new toasts
        for (const toast of currentToasts) {
          if (!this.trackedIds.has(toast.id) && !toast.isLoading && toast.countdown > 0) {
            this.startCountdown(toast);
            this.trackedIds.add(toast.id);
          }
        }
      },
      { allowSignalWrites: true }
    );
  }

  /** Get icon path for a toast type */
  getIcon(type: string): string {
    switch (type) {
      case 'success':
        return 'assets/icons/toast/success.svg';
      case 'error':
        return 'assets/img/wrong.png';
      case 'warning':
        return 'assets/icons/toast/warning.svg';
      case 'info':
        return 'assets/icons/toast/info.svg';
      default:
        return 'assets/icons/toast/info.svg';
    }
  }

  /** Get progress percentage for a specific toast */
  getProgress(toast: Toast): number {
    const progress = this.countdownProgress();
    return progress.get(toast.id) ?? 100;
  }

  /** Dismiss a specific toast */
  dismissToast(id: number): void {
    this.clearTimer(id);
    this.toastService.dismissById(id);
  }

  /** Execute toast action and dismiss */
  executeAction(toast: Toast): void {
    if (toast.action?.callback) {
      toast.action.callback();
    }
    this.dismissToast(toast.id);
  }

  /** Track toasts by ID for efficient rendering */
  trackById(_index: number, toast: Toast): number {
    return toast.id;
  }

  hasDescription(message: string): boolean {
    return message ? message.includes('\n') : false;
  }

  getTitle(message: string): string {
    if (!message) return '';
    return message.split('\n')[0];
  }

  getDescription(message: string): string {
    if (!message) return '';
    return message.split('\n')[1] || '';
  }

  // ── Touch swipe-to-dismiss ─────────────────

  private touchStartX = 0;
  private touchStartY = 0;
  private swipingToastId: number | null = null;

  onTouchStart(event: TouchEvent, toastId: number): void {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.swipingToastId = toastId;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.swipingToastId === null) return;

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = Math.abs(event.changedTouches[0].clientY - this.touchStartY);

    // Horizontal swipe > 80px and mostly horizontal
    if (Math.abs(deltaX) > 80 && deltaY < 50) {
      this.dismissToast(this.swipingToastId);
    }

    this.swipingToastId = null;
  }

  // ── Countdown management ───────────────────

  private startCountdown(toast: Toast): void {
    const totalMs = toast.countdown * 1000;
    const startTime = Date.now();
    const intervalMs = 50; // Update every 50ms for smooth progress bar

    // Initialize progress at 100%
    this.updateProgress(toast.id, 100);

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, totalMs - elapsed);
      const progress = (remaining / totalMs) * 100;

      this.updateProgress(toast.id, progress);

      if (remaining <= 0) {
        this.clearTimer(toast.id);
        this.toastService.dismissById(toast.id);
      }
    }, intervalMs);

    this.countdownTimers.set(toast.id, timer);
  }

  private updateProgress(id: number, value: number): void {
    this.countdownProgress.update(map => {
      const newMap = new Map(map);
      newMap.set(id, value);
      return newMap;
    });
  }

  private clearTimer(id: number): void {
    const timer = this.countdownTimers.get(id);
    if (timer) {
      clearInterval(timer);
      this.countdownTimers.delete(id);
    }
    this.trackedIds.delete(id);
  }

  ngOnDestroy(): void {
    for (const [id] of this.countdownTimers) {
      this.clearTimer(id);
    }
  }
}
